/**
 * ReferralStats - Displays referral statistics in three animated stat cards.
 * Shows completed referrals, pending rewards, and total savings.
 */

import type { ReferralStats as ReferralStatsType } from "../../hooks/useReferrals";

export interface ReferralStatsProps {
  stats: ReferralStatsType | null;
}

export function ReferralStats({ stats }: ReferralStatsProps) {
  if (!stats) {
    return (
      <div className="grid grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-28 rounded-2xl bg-neutral-100 animate-pulse"
          />
        ))}
      </div>
    );
  }

  const statCards = [
    {
      value: stats.completedReferrals,
      label: "Friends Joined",
      icon: UsersIcon,
      gradient: "from-magic-100 to-magic-200",
      border: "border-magic-300",
      iconBg: "bg-magic-400",
      valueColor: "text-magic-700",
    },
    {
      value: stats.pendingReferrals,
      label: "Pending",
      icon: ClockIcon,
      gradient: "from-sunshine-100 to-sunshine-200",
      border: "border-sunshine-300",
      iconBg: "bg-sunshine-400",
      valueColor: "text-sunshine-700",
    },
    {
      value: `$${(stats.totalRewardValueCents / 100).toFixed(0)}`,
      label: "Saved",
      icon: SparkleIcon,
      gradient: "from-meadow-100 to-meadow-200",
      border: "border-meadow-300",
      iconBg: "bg-meadow-400",
      valueColor: "text-meadow-700",
    },
  ];

  return (
    <div className="grid grid-cols-3 gap-4">
      {statCards.map((card, index) => (
        <div
          key={card.label}
          className={`
            relative overflow-hidden
            rounded-2xl border-[3px] ${card.border}
            bg-gradient-to-br ${card.gradient}
            p-4
            transition-all duration-300
            hover:scale-[1.02] hover:shadow-lg
          `}
          style={{
            animationDelay: `${index * 100}ms`,
          }}
        >
          {/* Icon badge */}
          <div
            className={`
              absolute -top-1 -right-1
              w-10 h-10 ${card.iconBg}
              rounded-bl-2xl rounded-tr-xl
              flex items-center justify-center
              shadow-md
            `}
          >
            <card.icon className="w-5 h-5 text-white" />
          </div>

          {/* Content */}
          <div className="relative z-10">
            <p
              className={`
                font-display font-bold text-display-md
                ${card.valueColor}
                leading-none
              `}
            >
              {card.value}
            </p>
            <p className="font-medium text-body-sm text-neutral-600 mt-1">
              {card.label}
            </p>
          </div>

          {/* Decorative circle */}
          <div
            className={`
              absolute -bottom-6 -left-6
              w-20 h-20 ${card.iconBg}
              rounded-full opacity-10
            `}
          />
        </div>
      ))}
    </div>
  );
}

// Icons
function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
    </svg>
  );
}

export default ReferralStats;
